/*
 * Card — the Hub's default surface (DESIGN.md §8).
 *
 * Off-white field, 1px line border, soft shadow, large radius. Enters with a
 * spring-soft rise from below (disabled under prefers-reduced-motion).
 *
 * Renders a motion.div by default. Pass `as="form"` (or any motion-supported
 * tag) when the card needs to be a different element, e.g. a sign-in form.
 */
import { motion, useReducedMotion } from 'framer-motion';
import type { HTMLMotionProps } from 'framer-motion';
import { cardVariants, enter } from '../lib/motion';

// Typed against form props (a superset of the div props we use) so `as="form"`
// can carry onSubmit cleanly. The element variable is cast to one component
// type below so the union doesn't trip strict tsc.
type CardProps = HTMLMotionProps<'form'> & {
  /** Render as a different motion element (e.g. "form"). Defaults to "div". */
  as?: 'div' | 'form';
};

const cardBase: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  padding: 'var(--space-5)',
  background: 'linear-gradient(180deg, var(--paper) 0%, var(--off-white) 100%)',
  border: '1px solid var(--silver-edge)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-md)',
  width: '100%',
  maxWidth: 360,
  textAlign: 'left',
};

export function Card({ as = 'div', style, children, ...rest }: CardProps) {
  const reduce = useReducedMotion();
  const MotionTag = (as === 'form' ? motion.form : motion.div) as typeof motion.form;
  return (
    <MotionTag
      {...enter(cardVariants, reduce)}
      style={{ ...cardBase, ...style }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

export default Card;
